import * as Sentry from '@sentry/browser';
import Game from 'Game';
import { SENTRY_EXTRA_STATE_DATA_KEY } from 'report';
import { GameScene } from 'State/Game/GameScene';
import { IGameState } from 'State/Game/IGameState';
import { ILevelState } from 'State/Level/ILevelState';
import { getHighestLevelCompleted } from 'State/Player/PlayerLevelHelper';
import { KeyOf } from 'Utility/Types';

type StateFilter = (state: IGameState) => {}

const StateSceneFilterMap: Record<GameScene, StateFilter[]> = {
    [GameScene.WORLD]: [
        getWorldSceneData
    ],
    [GameScene.LEVEL]: [
        getLevelSceneData
    ],
    [GameScene.LUCKY_TURNIPS]: [
        getLuckyTurnipsSceneData
    ],
    [GameScene.MY_PET_MINI_GAME]: [
        getMyPetSceneData
    ],
    [GameScene.FARM]: [
        getFarmSceneData
    ],
    [GameScene.PLINKO]: [
        getPlinkoSceneData
    ],
}

/**
 * 
 * @param event 
 * @returns 
 */
export function filterStateData(event: Sentry.Event): {} {
    let filteredState: {} = {};
    try {
        const state: IGameState = event.extra[SENTRY_EXTRA_STATE_DATA_KEY] ?? Game.store.getState();
        const scene = state.activeScene.result;

        filteredState = StateSceneFilterMap[scene].reduce((stateData: {}, filter: StateFilter) => {
            return {
                ...stateData,
                ...filter(state)
            }
        }, getCoreStateData(state));
    } catch (err) {
        event.extra = {
            ...event.extra,
            'redux-state': {
                message: 'State not available.',
                error: err,
                errorMsg: err?.message ?? err?.ToString()
            },
        };
    }
    return filteredState;
}

/**
 * Filters the GameState for the core meta data needed for each event 
 */
function getCoreStateData(state: IGameState): {} {
    return {
        scene: state.activeScene,
        entryPoint: state.entryPoint,
        highestLevelCompleted: getHighestLevelCompleted(state.playerState),
    }
}

/**
 * Filters the GameState for data relevant to the World scene
 */
function getWorldSceneData(state: IGameState): unknown {
    return {
        worldNumber: state.worldState.worldNumber
    }
}

/**
 * Filters the GameState for data relevant to the Level scene
 */
function getLevelSceneData(state: IGameState): unknown {
    const keys: KeyOf<ILevelState>[] = [
        'levelNumber', 'attempts', 'status', 'winCriteria', 'completeData', 'boosterUses', 'boosterPrices', 'helperFriendStatus', 'preBoosters', 'score'
    ]
    return keys.reduce((data, key) => {
        return {
            ...data,
            [key]: state.levelState[key]
        }
    }, {});
}

/**
 * Filters the GameState for data relevant to the Lucky Turnips scene
 */
function getLuckyTurnipsSceneData(state: IGameState): unknown {
    return {}
}

/**
 * Filters the GameState for data relevant to the MyPet scene
 */
function getMyPetSceneData(state: IGameState): unknown {
    return {}
}

/**
 * Filters the GameState for data relevant to the Farm scene
 */
function getFarmSceneData(state: IGameState): unknown {
    return {}
}

/**
 * Filters the GameState for data relevant to the Plinko scene
 */
 function getPlinkoSceneData(state: IGameState): unknown {
    return {}
}
